import React, { useRef } from "react";
import { useState } from "react";
import { useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Grid, Box, CssBaseline } from "@mui/material";

import DetailsPageHeader from "../../../../common/DetailsPageHeader";
import { mainLayoutStyles, showNotification } from "../../../../constants";
import Sidebar from "../../../../common/Sidebar";
import ImagePreviewModal from "../../../../common/ImagePreviewModal";
import AudioModal from "../../../../common/AudioModal";
import ConfirmationModal from "../../../../common/ConfirmationModal";
import { httpsCallable } from 'firebase/functions';
import { functions, auth } from '../../../../firebase';
import {
  QTDetailsDataFieldsSection,
  QTDetailsInteractionsFieldsSection,
} from "../../components/components";
import {
  updateQuickTale,
  uploadQuickTaleAudioToStorage,
  uploadQuickTaleImageToStorage,
  uploadProcessedAudioToStorage,
  getSingleQuickTale,
  getSingleUser,
} from "../../hooks/QuickTalesHooks";
import { resolveCreatedByData } from "../../../../utils/createdByUtils";


import {
  getAreaMembershipDiff,
  removeQuickTaleFromAlbumsAndQTs,
  upsertQuickTaleInAlbumsAndQTs,
} from "../../components/QTDetailsDataFieldsSection/QTAreasField";
import { getOrgAreas, updateArea } from "../../../AreaManagement/hooks/AreaHook";
import {
  createCustomNarrator,
  narratorNameExists,
  getCustomNarratorsByUser,
  getCustomNarratorByName,
  updateCustomNarrator,
} from "../../hooks/CustomNarratorsHooks";
import { useGetEmailTemplates } from "../../../OrganizationEmailTemplates/hooks/EmailTemplatesHooks";
import { useShareActivityEvents } from "../../../OrganizationBatches/hooks/BatchQuickTalesHooks";
import ShareActivityTable from "../../../OrganizationBatches/components/ShareActivityTable/ShareActivityTable";
import { useQuickTalesContext } from "../../../../providers/OrganizationAdmin/QuickTalesProvider";
import { useOrganizationContext } from "../../../../providers/OrganizationAdmin/OrganizationProvider";
import { Toast } from "../../../../common/alert";
import { useAuth } from "../../../../hooks";
import { GeoPoint } from "firebase/firestore";
import moment from "moment";
import { insertQTAddSearchDocumentData } from "../../../../firebase";
import { routes } from "../../../../constants";

const QuickTaleDetailsContainer = () => {
  const { currentUser } = useAuth();
  const {
    handleGetAllQuickTales,
    loading,
    setLoading,
    handleGetGAQuickTales,
    // insertQTAddSearchDocumentData,
  } = useQuickTalesContext();
  const { currentOrganization } = useOrganizationContext();
  const location = useLocation();
  const navigate = useNavigate();
  const isSaving = useRef(false);
  const { quicktaleId: paramQuicktaleId } = useParams(); // Get ID from URL params
  const [quicktaleDetails, setQuicktaleDetails] = useState(
    location?.state?.quicktaleDetails?.id
      ? location?.state?.quicktaleDetails
      : {}
  );
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [audioModal, setAudioModal] = useState(false);
  const [processedAudioModal, setProcessedAudioModal] = useState(false);
  const [editEnabled, setEditEnabled] = useState(false);
  const [uploadImage, setUploadImage] = useState(null);
  const [uploadAudioFile, setUploadAudioFile] = useState(null);
  const [uploadProcessedAudioFile, setUploadProcessedAudioFile] = useState(null);
  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] = useState(false);
  const [emailPromptModalOpen, setEmailPromptModalOpen] = useState(false);
  const [narratorEmail, setNarratorEmail] = useState("");
  const [narratorFirstName, setNarratorFirstName] = useState("");
  const [narratorLastName, setNarratorLastName] = useState("");
  const [customNarratorDoc, setCustomNarratorDoc] = useState(null);
  const [getLinkModalOpen, setGetLinkModalOpen] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [isCopied, setIsCopied] = useState(false);
  const [narratorError, setNarratorError] = useState(false);

  // Keep a baseline for area membership so we can diff on Save (org-only)
  const initialAreaIdsRef = useRef(null);
  
  // Email template selection states
  const [templateSelectionModalOpen, setTemplateSelectionModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [pendingNarratorData, setPendingNarratorData] = useState(null); // Stores narrator data while template is being selected

  // Get Link template selection states
  const [getLinkTemplateSelectionModalOpen, setGetLinkTemplateSelectionModalOpen] = useState(false);
  const [selectedGetLinkTemplate, setSelectedGetLinkTemplate] = useState("");
  
  // Fetch email templates for the organization
  const { templates: allEmailTemplates, loading: templatesLoading } = useGetEmailTemplates(quicktaleDetails?.organization);
  
  // Filter to only show active templates
  const activeEmailTemplates = allEmailTemplates?.filter(t => t.status === "active") || [];

  // Share activity tracking — real-time listener for the subcollection
  const { events: shareActivityEvents, loading: shareActivityLoading } = useShareActivityEvents(quicktaleDetails?.id, 'quicktales');

  // Auto-select default email template (or single active template) when a template
  // selection modal is opened and no template has been chosen yet.
  useEffect(() => {
    if (!(templateSelectionModalOpen || emailPromptModalOpen)) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedTemplate) return; // Don't override user choice

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedTemplate(activeEmailTemplates[0].id);
    }
  }, [
    templateSelectionModalOpen,
    emailPromptModalOpen,
    activeEmailTemplates,
    selectedTemplate,
  ]);

  // Auto-select default email template (or single active template) when the Get Link
  // template selection modal is opened and no template has been chosen yet.
  useEffect(() => {
    if (!getLinkTemplateSelectionModalOpen) return;
    if (!activeEmailTemplates || activeEmailTemplates.length === 0) return;
    if (selectedGetLinkTemplate) return;

    const defaultTemplate = activeEmailTemplates.find((t) => t.default === true);
    if (defaultTemplate) {
      setSelectedGetLinkTemplate(defaultTemplate.id);
    } else if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }
  }, [
    getLinkTemplateSelectionModalOpen,
    activeEmailTemplates,
    selectedGetLinkTemplate,
  ]);

  // Helper: fire-and-forget call to processAudioWithAuphonicV5 (HTTP onRequest function)
  const fireAndForgetProcessAudio = async ({ fileUrl, qtTitle, ogFileName, collectionName, documentId }) => {
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) {
        console.error("No auth token available for background audio processing.");
        return;
      }
      const projectId = auth.app.options.projectId;
      const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/processAudioWithAuphonicV5`;
      
      fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          fileUrl,
          qtTitle: qtTitle || 'Audio Processing',
          ogFileName: ogFileName || 'audio',
          collectionName,
          documentId,
        }),
      }).catch(err => console.error('Background audio processing error:', err));
      
      console.log("Fire-and-forget audio processing initiated for:", documentId);
    } catch (err) {
      console.error("Error initiating background audio processing:", err);
    }
  };

  // Manual "Process Original Audio" button handler — fires V5 in the background
  const handleProcessOriginalAudio = async () => {
    if (!quicktaleDetails.audioUrl) {
      Toast("error", "No original audio file to process.");
      return;
    }
    Toast("success", "Audio processing started in the background.");
    await fireAndForgetProcessAudio({
      fileUrl: quicktaleDetails.audioUrl,
      qtTitle: quicktaleDetails.title,
      ogFileName: quicktaleDetails.originalAudioName,
      collectionName: 'quicktales',
      documentId: quicktaleDetails.id,
    });
  };

  const handleFileUpload = async (file, type, source = "upload") => {
    if (!file) return;

    setLoading(true);
    try {
      let audioUrl;
      let dataTemp = {};

      if (type === "original") {
        const audioUrl = await uploadQuickTaleAudioToStorage(file.raw);
        dataTemp = {
          audioUrl: audioUrl,
          originalAudioName: file.raw.name,
        };
        
        // Create an audio element to get the duration
        const audio = new Audio(audioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration); // Duration in seconds
            dataTemp.audioLength = duration;
            setQuicktaleDetails((prev) => ({ 
              ...prev, 
              ...dataTemp,
              audioLength: duration,
              // Clear old processed audio — it belongs to the previous original audio
              processedAudioUrl: '',
              processedAudioName: '',
            }));
            resolve();
          };
        });
        
        // Show toast instead of opening the process audio confirmation modal
        const toastMessage = source === "record"
          ? "Audio recorded successfully! Click Save to update the quicktale."
          : "Audio uploaded successfully! Click Save to update the quicktale.";
        showNotification(toastMessage);
      } else if (type === "processed") {
        const processedAudioUrl = await uploadProcessedAudioToStorage(file.raw);
        dataTemp = {
          processedAudioUrl: processedAudioUrl,
          processedAudioName: file.raw.name,
        };
        
        // Create an audio element to get the duration
        const audio = new Audio(processedAudioUrl);
        await new Promise((resolve) => {
          audio.onloadedmetadata = () => {
            const duration = Math.floor(audio.duration); // Duration in seconds
            dataTemp.audioLength = duration;
            setQuicktaleDetails((prev) => ({ 
              ...prev, 
              ...dataTemp,
              audioLength: duration 
            }));
            resolve();
          };
        });
        
        showNotification(`Processed audio file uploaded successfully.`);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      Toast("error", `Error uploading file: ${error.message}`);
      
    } finally {
      setLoading(false);
    }
  };

  // console.log("qt details::", quicktaleDetails)
  const handleImagePreviewModalOpen = () => {
    setImagePreviewModal(true);
  };
  const handleImagePreviewModalClose = () => {
    setImagePreviewModal(false);
  };

  const handleAudioModalOpen = () => {
    setAudioModal(true);
  };
  const handleAudioModalClose = () => {
    setAudioModal(false);
  };

  const handleProcessedAudioModalOpen = () => {
    setProcessedAudioModal(true);
  };
  const handleProcessedAudioModalClose = () => {
    setProcessedAudioModal(false);
  };

  const handleCancelClick = () => {
    setCancelConfirmationModalOpen(true);
  };

  const handleConfirmCancel = () => {
    setCancelConfirmationModalOpen(false);
    setEditEnabled(false);
    // Reset any uploaded files
    setUploadImage(null);
    setUploadAudioFile(null);
    setUploadProcessedAudioFile(null);
    // Refresh the data to remove any unsaved changes by resetting to original state
    setQuicktaleDetails(location.state.quicktaleDetails);
  };

  useEffect(() => {
    const fetchQuickTale = async (id) => {
      console.log("Attempting to fetch QuickTale with ID:", id);
      setLoading(true);
      try {
        const doc = await getSingleQuickTale(id);
        if (doc.exists()) {
          console.log("QuickTale fetched successfully:", doc.id, doc.data());
          const docData = doc.data();
          
          // Fetch narrator data.
          const createdById = docData.createdBy;
          const narratorTemp = await getSingleUser(docData.narrator);

          // Resolve "Created By": the stored id can be a user OR an organization.
          // Shared helper tries users first, then falls back to organizations.
          // _sourceType is a display-only tag and is never persisted.
          const resolvedCreatedByData = await resolveCreatedByData(createdById);


          setQuicktaleDetails({
            id: doc.id,
            ...docData,
            createdByData: resolvedCreatedByData,
            narratorData: narratorTemp?.data() ? narratorTemp.data() : null,
            // Initialize userNoProcess based on processedAudioUrl
            userNoProcess: docData.processedAudioUrl ? false : true,
          });
        } else {
          console.error("QuickTale not found in DB for ID:", id);
          navigate('/quick-tales-in-organization', { replace: true });
        }
      } catch (error) {
        console.error("Error fetching QuickTale from DB:", error);
        navigate('/quick-tales-in-organization', { replace: true });
      } finally {
        setLoading(false);
      }
    };
    // Check if we have no data and no valid ID - redirect to parent page
    if (!location?.state?.quicktaleDetails?.id && (!paramQuicktaleId || paramQuicktaleId === ":quicktaleId")) {
      navigate('/quick-tales-in-organization', { replace: true });
      return;
    }

    if (paramQuicktaleId && paramQuicktaleId !== ":quicktaleId" && paramQuicktaleId !== quicktaleDetails.id) {
      fetchQuickTale(paramQuicktaleId);
    } else if (location?.state?.quicktaleDetails) {
      setQuicktaleDetails(location.state.quicktaleDetails);
    }
  }, [paramQuicktaleId, location.state, navigate]);

  // Subscribe to org areas and compute baseline membership for this QT.
  // We keep this in the container so we can persist membership changes on Save.
  useEffect(() => {
    const hydrateAreas = async () => {
      try {
        const orgId = quicktaleDetails?.organization;
        const qtId = quicktaleDetails?.id;
        if (!orgId || !qtId) return;

        const snapshot = await getOrgAreas(orgId);
        const allAreas = snapshot?.docs?.map((d) => ({ id: d.id, ...d.data() })) || [];
        const areaIds = allAreas
          .filter((a) =>
            (a?.albumsAndQTs || []).some(
              (x) => x?.type === "quicktales" && x?.id === qtId
            )
          )
          .map((a) => a.id);

        if (initialAreaIdsRef.current == null) {
          initialAreaIdsRef.current = areaIds;
        }

        setQuicktaleDetails((prev) => {
          // Don't clobber user edits if already in edit mode
          if (editEnabled && Array.isArray(prev?.uiAreaIds)) return prev;
          return { ...prev, uiAreaIds: areaIds };
        });
      } catch (e) {
        console.warn("Unable to hydrate QT areas", e);
      }
    };

    hydrateAreas();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quicktaleDetails?.organization, quicktaleDetails?.id]);

  // Don't render anything if we don't have valid quicktale data
  if (!quicktaleDetails?.id) {
    return null;
  }

  const handleBatchNameClick = () => {
    if (!quicktaleDetails?.batchId) return;
    navigate(routes.batchDetails.replace(":batchId", quicktaleDetails.batchId));
  };

  // Persist a "Created By" change immediately (used by the "Set to Org" button
  // in view mode, where there is no Save button). Stores just the id.
  const handleCreatedBySet = async (newCreatedById) => {
    if (!quicktaleDetails?.id || !newCreatedById) return;
    try {
      await updateQuickTale(quicktaleDetails.id, {
        createdBy: newCreatedById,
        updatedAt: new Date(),
        updatedBy: currentUser?.id || "",
      });
      showNotification("Created By updated successfully!");
    } catch (error) {
      console.error("Error updating Created By:", error);
      Toast("error", `Error updating Created By: ${error.message}`);
    }
  };

  const sendLinkToNarrator = async (email, firstName, lastName, templateId) => {
    setLoading(true);
    try {
      const sendExternalUpdateLink = httpsCallable(functions, 'sendExternalUpdateLink');
      await sendExternalUpdateLink({
        quicktaleId: quicktaleDetails.id,
        email: email,
        firstName: firstName,
        lastName: lastName,
        quickTale: {
          title: quicktaleDetails.title,
        },
        origin: window.location.origin,
        emailTemplateId: templateId,
        orgName: currentOrganization?.name || "",
        collection: 'quicktales',
      });
      showNotification("Update link sent successfully.");
    } catch (error) {
      console.error("Error calling sendExternalUpdateLink:", error);
      Toast("error", `Error sending link: ${error.message}`);
      
    } finally {
      setLoading(false);
      // Reset template selection
      setSelectedTemplate("");
      setPendingNarratorData(null);
    }
  };

  const handleSendLink = async () => {
    if (!quicktaleDetails.narrator) {
      showNotification("Told By is required to send a link.");
      return;
    }
    console.log('Narrator: ', quicktaleDetails.narrator);
    // Case 1: Narrator is a registered user with email
    if (quicktaleDetails.narratorData && quicktaleDetails.narratorData.email) {
      // Store narrator data and open template selection modal
      setPendingNarratorData({
        email: quicktaleDetails.narratorData.email,
        firstName: quicktaleDetails.narratorData.firstName,
        lastName: quicktaleDetails.narratorData.lastName,
      });
      // Auto-select if only one template
      if (activeEmailTemplates.length === 1) {
        setSelectedTemplate(activeEmailTemplates[0].id);
      }
      setTemplateSelectionModalOpen(true);
      return;
    }

    // Case 2: Narrator is a custom narrator
    if (!quicktaleDetails.narratorData && quicktaleDetails.narrator) {
      const narratorName = quicktaleDetails.narrator.trim();
      const userIdToUse = currentUser?.userId || currentUser?.uid;
      
      try {
        const customNarrator = await getCustomNarratorByName(
          narratorName,
          userIdToUse
        );

        // Always prompt for narrator details so First Name, Last Name, and
        // Email Address are visible and editable, regardless of whether
        // values already exist.
        setCustomNarratorDoc(customNarrator);
        setNarratorEmail(customNarrator?.email || "");
        setNarratorFirstName(customNarrator?.firstName || "");
        setNarratorLastName(customNarrator?.lastName || "");
        setEmailPromptModalOpen(true);
      } catch (error) {
        console.error("Error fetching custom narrator:", error);
        Toast("error", "Could not find custom narrator.");
        
      }
      return;
    }
    console.error("Line 406 - Narrator has no email address.");
    Toast("error", "Narrator has no email address.");
    
  };

  // Handle template selection modal continue
  const handleTemplateSelectionContinue = async () => {
    if (!selectedTemplate) {
      showNotification("Please select an email template.");
      return;
    }
    
    setTemplateSelectionModalOpen(false);
    
    if (pendingNarratorData) {
      await sendLinkToNarrator(
        pendingNarratorData.email,
        pendingNarratorData.firstName,
        pendingNarratorData.lastName,
        selectedTemplate
      );
    }
  };

  // Handle template selection modal close
  const handleTemplateSelectionClose = () => {
    setTemplateSelectionModalOpen(false);
    setSelectedTemplate("");
    setPendingNarratorData(null);
  };

  const handleEmailPromptContinue = async () => {
    // Validate all required fields
    if (!narratorFirstName) {
      showNotification("Please enter a first name.");
      return;
    }
    if (!narratorEmail) {
      showNotification("Please enter an email address.");
      return;
    }
    // Validate template selection (if templates are available)
    if (activeEmailTemplates.length > 0 && !selectedTemplate) {
      showNotification("Please select an email template.");
      return;
    }
    
    setEmailPromptModalOpen(false);

    try {
      // Update the custom narrator record with all the new details
      if (customNarratorDoc && customNarratorDoc.id) {
        await updateCustomNarrator(customNarratorDoc.id, { 
          email: narratorEmail,
          firstName: narratorFirstName,
          lastName: narratorLastName 
        });
        await sendLinkToNarrator(narratorEmail, narratorFirstName, narratorLastName, selectedTemplate);
      } else {
        throw new Error("Custom narrator document not found.");
      }
    } catch (error) {
      console.error("Error updating custom narrator:", error);
      Toast("error", `Failed to save narrator details: ${error.message}`);
      
    }
    // Reset all inputs
    setNarratorEmail("");
    setNarratorFirstName("");
    setNarratorLastName("");
    setSelectedTemplate("");
  };
  
  // Handle email prompt modal close
  const handleEmailPromptClose = () => {
    setEmailPromptModalOpen(false);
    setNarratorEmail("");
    setNarratorFirstName("");
    setNarratorLastName("");
    setSelectedTemplate("");
    setCustomNarratorDoc(null);
  };

  const handleGetLink = async () => {
    setIsCopied(false); // Reset copied state when opening modal

    // Templates are still being fetched — don't show the "no templates" error yet
    if (templatesLoading) return;

    if (!activeEmailTemplates || activeEmailTemplates.length === 0) {
      showNotification("Please create an email template");
      return;
    }

    // If there is only one active template, pre-select it
    if (activeEmailTemplates.length === 1) {
      setSelectedGetLinkTemplate(activeEmailTemplates[0].id);
    }

    setGetLinkTemplateSelectionModalOpen(true);
  };

  const handleGetLinkTemplateSelectionContinue = async () => {
    if (!selectedGetLinkTemplate) {
      showNotification("Please select an email template.");
      return;
    }

    setGetLinkTemplateSelectionModalOpen(false);
    setLoading(true);

    try {
      // Resolve narrator info for activity logging
      let narratorNameForLog = '';
      let narratorEmailForLog = '';
      if (quicktaleDetails.narratorData) {
        narratorNameForLog = `${quicktaleDetails.narratorData.firstName || ''} ${quicktaleDetails.narratorData.lastName || ''}`.trim();
        narratorEmailForLog = quicktaleDetails.narratorData.email || '';
      } else if (quicktaleDetails.narrator) {
        // Look up custom narrator to get email
        narratorNameForLog = quicktaleDetails.narrator;
        try {
          const userIdToUse = currentUser?.userId || currentUser?.uid;
          const cn = await getCustomNarratorByName(quicktaleDetails.narrator.trim(), userIdToUse);
          if (cn) {
            narratorNameForLog = cn.name || quicktaleDetails.narrator;
            narratorEmailForLog = cn.email || '';
          }
        } catch (e) {
          console.warn('Get Link: unable to resolve custom narrator for activity logging', e);
        }
      }

      const generateExternalUpdateLink = httpsCallable(functions, 'generateExternalUpdateLink');
      const result = await generateExternalUpdateLink({
        quicktaleId: quicktaleDetails.id,
        origin: window.location.origin,
        collection: 'quicktales',
        emailTemplateId: selectedGetLinkTemplate,
        narratorName: narratorNameForLog,
        narratorEmail: narratorEmailForLog,
      });

      const { link } = result.data;
      if (link) {
        setGeneratedLink(link);
        setGetLinkModalOpen(true);
      } else {
        throw new Error("Could not generate link.");
      }
    } catch (error) {
      console.error("Error calling generateExternalUpdateLink:", error);
      Toast("error", `Error generating link: ${error.message}`);
    } finally {
      setLoading(false);
      setSelectedGetLinkTemplate("");
    }
  };

  const handleGetLinkTemplateSelectionClose = () => {
    setGetLinkTemplateSelectionModalOpen(false);
    setSelectedGetLinkTemplate("");
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generatedLink);
    setIsCopied(true);
    // Keep modal open, but show "Copied!" text
    setTimeout(() => {
        setGetLinkModalOpen(false);
        setIsCopied(false); // Reset for next time
    }, 1500); // Close modal after 1.5s
  };


  const handleUpdateQuickTale = async () => {
    if (!quicktaleDetails.narrator) {
      setNarratorError(true);
      showNotification("Told by is a required field.");
      return;
    } else {
      setNarratorError(false);
    }
    try {
      setLoading(true);
      isSaving.current = true;
      
      // Check if narrator is a custom name (text) and not a user ID
      // If narratorData is null, it means they typed text or selected a custom narrator
      let customNarratorCreated = false;
      if (!quicktaleDetails.narratorData && quicktaleDetails.narrator) {
        const narratorName = quicktaleDetails.narrator.trim();
        
        // Get user ID to check/create custom narrators
        const userIdToUse = currentUser?.userId || currentUser?.uid;
        
        if (userIdToUse && narratorName) {
          try {
            // Fetch current custom narrators for this user
            const existingNarrators = await getCustomNarratorsByUser(userIdToUse);
            
            // Check if this narrator name already exists
            if (!narratorNameExists(narratorName, existingNarrators)) {
              // Create new custom narrator
              const newNarrator = await createCustomNarrator(narratorName, userIdToUse);
              if (newNarrator) {
                console.log("Created new custom narrator:", newNarrator);
                customNarratorCreated = true;
              }
            }
          } catch (error) {
            console.error("Error checking/creating custom narrator:", error);
            // Continue with save even if custom narrator creation fails
          }
        }
      }
      // let dataTemp = {
      //   title: quicktaleDetails.title,
      //   description: quicktaleDetails.description,
      //   status: quicktaleDetails.status,
      //   approvalStatus: quicktaleDetails?.approvalStatus,
      //   updatedAt: new Date(),
      //   updatedBy: currentUser?.id,
      //   createdBy: quicktaleDetails.createdBy,
      //   narrator: quicktaleDetails.narrator,
      //   // location: quicktaleDetails.location,
      //   // // coordinates: quicktaleDetails.coordinates,
      //   // // g: quicktaleDetails.g,
      //   // coordinates: new GeoPoint(
      //   //   quicktaleDetails?.coordinates?._lat,
      //   //   quicktaleDetails?.coordinates?._long
      //   // ),
      //   // g: {
      //   //   geohash: quicktaleDetails?.g?.geohash,
      //   //   geopoint: new GeoPoint(
      //   //     quicktaleDetails?.coordinates?._lat,
      //   //     quicktaleDetails?.coordinates?._long
      //   //   ),
      //   // },
      //   tags:
      //     quicktaleDetails.tags === location.state.quicktaleDetails.tags
      //       ? location.state.quicktaleDetails.tags
      //       : quicktaleDetails.tags.split(","),
      // };
      let title =
        quicktaleDetails.title || `Untitled ${moment().format("MM-DD-YYYY")}`;
      // quicktaleDetails.title ||
      // `Untitled ${new Date().toLocaleDateString("en-US", {
      //   month: "2-digit",
      //   day: "2-digit",
      //   year: "numeric",
      // })}`;
      setQuicktaleDetails({ ...quicktaleDetails, title: title });
      let dataTemp = {
        title: title,
        // title: quicktaleDetails?.title || "",
        description: quicktaleDetails?.description || "",
        status: quicktaleDetails?.status || "",
        isDraft:
          quicktaleDetails?.isDraft !== undefined
            ? quicktaleDetails.isDraft
            : quicktaleDetails?.title?.includes("Untitled") || false,
        approvalStatus: quicktaleDetails?.approvalStatus || "",
        updatedAt: new Date(),
        updatedBy: currentUser?.id || "",
        createdBy: quicktaleDetails?.createdBy || "",
        narrator: quicktaleDetails?.narrator || "",
        location: quicktaleDetails?.location || "",
        locationDisplay: quicktaleDetails?.locationDisplay || "",
        locationCountry: quicktaleDetails?.locationCountry || "",
        coordinates: new GeoPoint(
          quicktaleDetails?.coordinates?._lat || 0,
          quicktaleDetails?.coordinates?._long || 0
        ),
        g: {
          geohash: quicktaleDetails?.g?.geohash || "",
          geopoint: new GeoPoint(
            quicktaleDetails?.coordinates?._lat || 0,
            quicktaleDetails?.coordinates?._long || 0
          ),
        },
        // tags:
        //   quicktaleDetails.tags === location.state.quicktaleDetails.tags
        //     ? location.state.quicktaleDetails.tags
        //     : quicktaleDetails?.tags.split(","),
      };
      if (quicktaleDetails.tags !== location?.state?.quicktaleDetails?.tags) {
        if (typeof quicktaleDetails.tags === "string") {
          dataTemp.tags = quicktaleDetails.tags.split(",");
        } else if (Array.isArray(quicktaleDetails.tags)) {
          dataTemp.tags = [...quicktaleDetails.tags];
        } else {
          dataTemp.tags = [];
        }
      }

      if (uploadImage != null) {
        let photoUrl = await uploadQuickTaleImageToStorage(uploadImage?.raw);
        dataTemp.imageUrls = [photoUrl];
        // dataTemp.imageUrls = photoUrl;
        dataTemp.originalPhotoName = uploadImage?.raw?.name;
      }
      if (quicktaleDetails.audioUrl) {
        dataTemp.audioUrl = quicktaleDetails.audioUrl;
        dataTemp.originalAudioName = quicktaleDetails.originalAudioName;
      }
      if (quicktaleDetails.processedAudioUrl) {
        dataTemp.processedAudioUrl = quicktaleDetails.processedAudioUrl;
      } else {
        dataTemp.processedAudioUrl = '';
      }
      if (quicktaleDetails.processedAudioName) {
        dataTemp.processedAudioName = quicktaleDetails.processedAudioName;
      } else {
        dataTemp.processedAudioName = '';
      }
      if (quicktaleDetails.audioLength) {
        dataTemp.audioLength = quicktaleDetails.audioLength;
      }
      // Save userNoProcess field
      if (quicktaleDetails.userNoProcess !== undefined) {
        dataTemp.userNoProcess = quicktaleDetails.userNoProcess;
      }
      console.log("data:", dataTemp);
      let responseTemp = await updateQuickTale(quicktaleDetails.id, dataTemp);
      
      showNotification("QuickTale updated successfully.");

      // Persist Areas membership changes (org-only)
      try {
        const orgId = quicktaleDetails?.organization;
        const qtId = quicktaleDetails?.id;
        if (orgId && qtId) {
          const snapshot = await getOrgAreas(orgId);
          const allAreas = snapshot?.docs?.map((d) => ({ id: d.id, ...d.data() })) || [];

          const { added, removed } = getAreaMembershipDiff({
            initialAreaIds: initialAreaIdsRef.current || [],
            currentAreaIds: Array.isArray(quicktaleDetails?.uiAreaIds)
              ? quicktaleDetails.uiAreaIds
              : [],
          });

          // Apply removals
          for (const areaId of removed) {
            const area = allAreas.find((a) => a.id === areaId);
            if (!area) continue;
            const updated = removeQuickTaleFromAlbumsAndQTs({
              albumsAndQTs: area?.albumsAndQTs || [],
              qtId,
            });
            await updateArea(areaId, { albumsAndQTs: updated, updatedAt: new Date() });
          }

          // Apply additions (append)
          for (const areaId of added) {
            const area = allAreas.find((a) => a.id === areaId);
            if (!area) continue;
            const updated = upsertQuickTaleInAlbumsAndQTs({
              albumsAndQTs: area?.albumsAndQTs || [],
              qtId,
            });
            await updateArea(areaId, { albumsAndQTs: updated, updatedAt: new Date() });
          }

          // Update baseline so subsequent edits diff correctly without refresh
          if (added.length || removed.length) {
            initialAreaIdsRef.current = Array.isArray(quicktaleDetails?.uiAreaIds)
              ? quicktaleDetails.uiAreaIds
              : [];
          }
        }
      } catch (e) {
        console.error("Error updating QT area membership", e);
      }
      
      // Update AddSearch index after successful Firestore update
      try {
        const updateQTInAddSearch = httpsCallable(functions, 'updateQTInAddSearch');
        
        const addSearchResult = await updateQTInAddSearch({
          docId: quicktaleDetails.id,
          data: {
            ...dataTemp,
            createdAt: quicktaleDetails.createdAt,
            creator: quicktaleDetails.createdBy,
            environment: process.env.REACT_APP_PROJECT_ENVIROMENT,
          },
          docType: "quicktale",
        });
        
        console.log("AddSearch update result:", addSearchResult.data);
      } catch (error) {
        console.error("Error updating QuickTale in AddSearch:", error);
      }
      
      // Update Clarifai if image was uploaded or edited
      // This triggers for both:
      // 1. New image uploads (uploadImage set when user uploads a file)
      // 2. Image edits (uploadImage set when user edits existing image via crop modal)
      if (uploadImage != null) {
        const newImageUrl = dataTemp.imageUrls[0]; // New URL from Firebase Storage upload

        // (a) Google Vertex AI vision embeddings — the new image-search system.
        //     Re-embeds imageUrls[0] and stores the vector on the QT doc.
        try {
          console.log("Re-indexing QuickTale image to Vision. Image URL:", newImageUrl);
          const indexSingleQuickTaleToVision = httpsCallable(functions, 'indexSingleQuickTaleToVision');

          const visionResult = await indexSingleQuickTaleToVision({
            quicktaleId: quicktaleDetails.id,
            imageUrl: newImageUrl,
          });

          console.log("Vision re-index result:", visionResult.data);
        } catch (error) {
          console.error("Error re-indexing QuickTale to Vision:", error);
        }

        // (b) Clarifai — legacy image-search system, kept during the transition.
        //     Clarifai is being retired; this call must fail silently so its
        //     eventual shutdown never blocks a QuickTale save.
        try {
          console.log("Updating Clarifai with new/edited image. Image URL:", newImageUrl);

          const updateClarifaiInput = httpsCallable(functions, 'updateClarifaiInput');

          const clarifaiResult = await updateClarifaiInput({
            quicktaleId: quicktaleDetails.id,
            imageUrl: newImageUrl, // New URL from Firebase Storage upload
            metadata: {
              ORG_ID: quicktaleDetails.organization || "",
              QTID: quicktaleDetails.id,
              description: quicktaleDetails.description || "",
              location: quicktaleDetails.location || "",
              locationDisplay: quicktaleDetails.locationDisplay || "",
              tags: Array.isArray(quicktaleDetails.tags) 
                ? quicktaleDetails.tags 
                : (typeof quicktaleDetails.tags === 'string' 
                  ? quicktaleDetails.tags.split(',') 
                  : []),
              title: title
            }
          });
          
          console.log("Clarifai update successful:", clarifaiResult.data);
        } catch (error) {
          console.error("Error updating QuickTale in Clarifai:", error);
        }
      } else {
        console.log("Clarifai update skipped - no image changes detected");
      }

      // Fire-and-forget: process audio in the background via V5
      // Triggers when: (a) there's an audioUrl but no processedAudioUrl, OR
      //                (b) new audio was uploaded/recorded in this edit session
      if (quicktaleDetails.audioUrl && (!quicktaleDetails.processedAudioUrl || uploadAudioFile != null)) {
        console.log("Initiating background audio processing via processAudioWithAuphonicV5");
        fireAndForgetProcessAudio({
          fileUrl: quicktaleDetails.audioUrl,
          qtTitle: title,
          ogFileName: quicktaleDetails.originalAudioName,
          collectionName: 'quicktales',
          documentId: quicktaleDetails.id,
        });
      }

      // Toast("success", "QuickTale updated successfully.");
    } catch (error) {
      console.log("error updating quicktale", error.message);
      Toast("error", error.message);
    } finally {
      setLoading(false);
      setEditEnabled(false);
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <DetailsPageHeader
        mainTitle={
          quicktaleDetails && quicktaleDetails.title && quicktaleDetails.title
        }
        bgColor="#fff"
        buttonText={"Edit"}
        editEnabled={editEnabled}
        setEditEnabled={setEditEnabled}
        cancelButton={editEnabled ? true : false}
        onCancelClick={handleCancelClick}
        handleSave={() => handleUpdateQuickTale()}
        loading={loading}
        showSendLink={!editEnabled}
        onSendLink={handleSendLink}
        showGetLink={!editEnabled}
        onGetLink={handleGetLink}
        hideBackArrow={editEnabled}
      />
      <Sidebar />
      <Grid container style={{ ...mainLayoutStyles, background: "#fff" }}>
        <Grid container direction={"column"} style={subContainerStyles}>
          {/*Data card/container section*/}
          <QTDetailsDataFieldsSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
            isSaving={isSaving}
            handleImagePreviewModalOpen={handleImagePreviewModalOpen}
            handleAudioModalOpen={handleAudioModalOpen}
            handleProcessedAudioModalOpen={handleProcessedAudioModalOpen}
            editEnabled={editEnabled}
            uploadImage={uploadImage}
            setUploadImage={setUploadImage}
            uploadAudioFile={uploadAudioFile}
            setUploadAudioFile={setUploadAudioFile}
            uploadProcessedAudioFile={uploadProcessedAudioFile}
            setUploadProcessedAudioFile={setUploadProcessedAudioFile}
            handleFileUpload={handleFileUpload}
            onProcessOriginalAudio={handleProcessOriginalAudio}
            narratorError={narratorError}
            onBatchNameClick={handleBatchNameClick}
            areasMode={"orgMulti"}
            currentOrganization={currentOrganization}
            onCreatedBySet={handleCreatedBySet}
          />
          {/*interactions card/container section*/}
          <QTDetailsInteractionsFieldsSection
            quicktaleDetails={quicktaleDetails}
            setQuicktaleDetails={setQuicktaleDetails}
          />

          {/* Contributor Request Activity section */}
          <Grid
            container
            direction={"column"}
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid #e0e0e0",
            }}
          >
            <h3 style={{ margin: 0, marginBottom: 12, fontFamily: "Avenir Book, sans-serif" }}>
              Contributor Request Activity
            </h3>
            <ShareActivityTable
              events={shareActivityEvents}
              loading={shareActivityLoading}
              templates={allEmailTemplates}
            />
          </Grid>
          <div style={{marginBottom: 100}}></div>
        </Grid>
      </Grid>
      <ImagePreviewModal
        modalOpen={imagePreviewModal}
        handleClose={handleImagePreviewModalClose}
        image={
          uploadImage != null && uploadImage.preview
            ? uploadImage.preview
            : quicktaleDetails.photoUrl
        }
      />
      <AudioModal
        modalOpen={audioModal}
        handleClose={handleAudioModalClose}
        audioFile={
          uploadAudioFile != null && uploadAudioFile.preview
            ? uploadAudioFile.preview
            : quicktaleDetails.audioUrl
        }
      />
      <AudioModal
        modalOpen={processedAudioModal}
        handleClose={handleProcessedAudioModalClose}
        audioFile={
          uploadProcessedAudioFile != null && uploadProcessedAudioFile.preview
            ? uploadProcessedAudioFile.preview
            : quicktaleDetails.processedAudioUrl
        }
      />
      <ConfirmationModal
        open={cancelConfirmationModalOpen}
        onClose={() => setCancelConfirmationModalOpen(false)}
        onConfirm={handleConfirmCancel}
        title="Discard Changes"
        message="Are you sure you want to cancel? All unsaved changes will be lost."
      />
      {/* Template Selection Modal - for when narrator already has email */}
      <ConfirmationModal
        open={templateSelectionModalOpen}
        onClose={handleTemplateSelectionClose}
        onConfirm={handleTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select a template for your email."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown={true}
        templates={activeEmailTemplates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={(e) => setSelectedTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
        // When narrator is a registered user, show their details as read-only
        narratorName={
          quicktaleDetails.narratorData
            ? quicktaleDetails.narratorData.userName
              ? `@${quicktaleDetails.narratorData.userName}`
              : `${quicktaleDetails.narratorData.firstName || ""} ${
                  quicktaleDetails.narratorData.lastName || ""
                }`.trim()
            : undefined
        }
        firstName={quicktaleDetails.narratorData?.firstName}
        lastName={quicktaleDetails.narratorData?.lastName}
        textFieldValue={quicktaleDetails.narratorData?.email}
        narratorReadOnly={!!quicktaleDetails.narratorData}
      />
      {/* Email Prompt Modal - for when narrator is missing details (with template dropdown) */}
      <ConfirmationModal
        open={emailPromptModalOpen}
        onClose={handleEmailPromptClose}
        onConfirm={handleEmailPromptContinue}
        title="Narrator Details Required"
        message="Please enter the missing details needed to send the update link."
        textFieldValue={narratorEmail}
        onTextFieldChange={(e) => setNarratorEmail(e.target.value)}
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        narratorName={customNarratorDoc?.name || quicktaleDetails.narrator}
        firstName={narratorFirstName}
        onFirstNameChange={(e) => setNarratorFirstName(e.target.value)}
        lastName={narratorLastName}
        onLastNameChange={(e) => setNarratorLastName(e.target.value)}
        showTemplateDropdown={true}
        templates={activeEmailTemplates}
        selectedTemplate={selectedTemplate}
        onTemplateChange={(e) => setSelectedTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />

      {/* Template Selection Modal - for Get Link (link-only, no send) */}
      <ConfirmationModal
        open={getLinkTemplateSelectionModalOpen}
        onClose={handleGetLinkTemplateSelectionClose}
        onConfirm={handleGetLinkTemplateSelectionContinue}
        title="Select Email Template"
        message="Please select a template for the narrator page settings."
        cancelButtonText="Cancel"
        confirmButtonText="Continue"
        showTemplateDropdown={true}
        templates={activeEmailTemplates}
        selectedTemplate={selectedGetLinkTemplate}
        onTemplateChange={(e) => setSelectedGetLinkTemplate(e.target.value)}
        noTemplatesMessage="Please create an email template"
      />

      <ConfirmationModal
        open={getLinkModalOpen}
        onClose={() => setGetLinkModalOpen(false)}
        onConfirm={handleCopyToClipboard}
        title="Link to update QuickTale photo, audio, title, and/or description"
        message="" // Message is now inside the text field
        confirmButtonText={isCopied ? "Copied!" : "Copy"}
        cancelButtonText="Close"
        showTextField={true}
        textFieldValue={generatedLink}
      />
    </Box>
  );
};

export default QuickTaleDetailsContainer;

const subContainerStyles = {
  paddingLeft: 30,
  paddingRight: 30,
  marginTop: 50,
};
